import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { mouvements, soldeAu } from '../../analysis/soldes'
import { RefusSuppression, enregistrerCompte, supprimerCompte } from '../../db/repo'
import { aujourdhui, formatJour } from '../../domain/temps'
import { TYPES_COMPTE, TYPE_COMPTE_LABELS, type Cents, type TypeCompte } from '../../domain/types'
import { Carte, Champ, ChampMontant, Confirmation, Encart, Entete, IconeChevron, IconePlus, Montant, Pastille, Puce, useAction } from '../composants'
import { useDonnees } from '../donnees'

export function PageComptes() {
  const { d, ix } = useDonnees()
  const navigate = useNavigate()
  const jour = aujourdhui()
  const mvts = useMemo(() => mouvements(ix), [ix])

  return (
    <>
      <Entete titre="Comptes" retour />
      <main className="contenu">
        <ul className="liste">
          {d.comptes.map((c) => (
            <li key={c.id}>
              <Link to={`/comptes/${c.id}`} className="item">
                <Pastille couleur={c.couleur} />
                <div className="grandir">
                  <div className="item__principal">{c.nom}</div>
                  <div className="item__secondaire">{TYPE_COMPTE_LABELS[c.type]}</div>
                </div>
                {c.archive === 1 && <Puce>archivé</Puce>}
                <Montant valeur={soldeAu(c, mvts, jour)} />
                <span className="chevron"><IconeChevron /></span>
              </Link>
            </li>
          ))}
        </ul>
        <Encart>
          Chaque dépense et chaque revenu est rattaché à un compte : la prime LAMal prélevée sur « Salaire », les courses payées depuis le
          compte courant. Les soldes et le prévisionnel se lisent ensuite compte par compte.
        </Encart>
      </main>
      <button type="button" className="fab" aria-label="Nouveau compte" onClick={() => navigate('/comptes/nouveau')}>
        <IconePlus />
      </button>
    </>
  )
}

export function PageCompte() {
  const { id } = useParams()
  const { d } = useDonnees()
  const navigate = useNavigate()
  const existant = d.comptes.find((c) => c.id === id)

  const [nom, setNom] = useState(existant?.nom ?? '')
  const [type, setType] = useState<TypeCompte>(existant?.type ?? 'courant')
  const [soldeInitial, setSoldeInitial] = useState<Cents | null>(existant?.soldeInitial ?? 0)
  const [soldeInitialLe, setSoldeInitialLe] = useState(existant?.soldeInitialLe ?? aujourdhui())
  const [couleur, setCouleur] = useState(existant?.couleur ?? '#4c8bf5')
  const [archive, setArchive] = useState<0 | 1>(existant?.archive ?? 0)
  const [confirmer, setConfirmer] = useState(false)
  const { erreur, executer, setErreur } = useAction()

  const enregistrer = () =>
    executer(async () => {
      if (!nom.trim()) throw new Error('Donnez un nom au compte.')
      await enregistrerCompte({
        id: existant?.id,
        nom: nom.trim(),
        type,
        soldeInitial: soldeInitial ?? 0,
        soldeInitialLe,
        couleur,
        archive,
        ordre: existant?.ordre ?? d.comptes.length,
      })
      navigate(-1)
    })

  return (
    <>
      <Entete titre={existant ? existant.nom : 'Nouveau compte'} retour />
      <main className="contenu">
        <Carte>
          <div className="pile">
            <Champ libelle="Nom">
              <input type="text" value={nom} placeholder="Salaire, courant, épargne…" onChange={(e) => setNom(e.target.value)} />
            </Champ>
            <Champ libelle="Type">
              <select value={type} onChange={(e) => setType(e.target.value as TypeCompte)}>
                {TYPES_COMPTE.map((t) => (
                  <option key={t} value={t}>{TYPE_COMPTE_LABELS[t]}</option>
                ))}
              </select>
            </Champ>
            <div className="grille-2">
              <Champ libelle="Solde de départ (CHF)">
                <ChampMontant valeur={soldeInitial} onChange={setSoldeInitial} negatif />
              </Champ>
              <Champ libelle="Au matin du">
                <input type="date" value={soldeInitialLe} onChange={(e) => setSoldeInitialLe(e.target.value)} />
              </Champ>
            </div>
            <p className="tres-doux">
              Lisez le solde sur votre e-banking à cette date : les opérations saisies à partir du {formatJour(soldeInitialLe)} inclus
              s’y ajoutent.
            </p>
            <Champ libelle="Couleur">
              <input type="color" value={couleur} onChange={(e) => setCouleur(e.target.value)} style={{ width: 64, height: 40, padding: 2 }} />
            </Champ>
            {existant && (
              <label className="case">
                <input type="checkbox" checked={archive === 1} onChange={(e) => setArchive(e.target.checked ? 1 : 0)} />
                Archivé (compte clôturé : masqué, mais son historique reste dans les statistiques)
              </label>
            )}
          </div>
        </Carte>
        {erreur && <Encart ton="danger">{erreur}</Encart>}
        <button type="button" className="btn btn--principal btn--bloc" onClick={() => void enregistrer()}>Enregistrer</button>
        {existant && <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmer(true)}>Supprimer</button>}
      </main>
      {confirmer && existant && (
        <Confirmation
          titre="Supprimer ce compte ?"
          message="Seul un compte sans aucune opération peut être supprimé."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => {
            setConfirmer(false)
            void supprimerCompte(existant.id)
              .then(() => navigate('/comptes', { replace: true }))
              .catch((e: unknown) => setErreur(e instanceof RefusSuppression || e instanceof Error ? e.message : String(e)))
          }}
        />
      )}
    </>
  )
}
